/**
 * SB6-II-01 — shared Project → Contract → Contract Position cascade.
 *
 * Used by the Add modal and by the Edit modal's re-link. Three
 * dependent single-selects:
 *   1. Project — `options/projects` (active-state set). Picking enables
 *      Contract; CHANGING it clears Contract + Position (no lock-in).
 *   2. Contract — `options/contracts?projectId=`; label
 *      `{contract_code} ({site})`. Picking enables Position; changing
 *      clears Position.
 *   3. Contract Position — `options/contract-positions?contractId=`;
 *      label = composed asset name.
 *
 * The component is controlled: the parent owns the selected ids and
 * gets notified via `onChange` of the full triple. Downstream selects
 * are disabled until their upstream is chosen.
 */
import { type ReactElement } from "react";
import {
  Autocomplete,
  Stack,
  TextField,
  type AutocompleteRenderInputParams,
} from "@mui/material";
import {
  useIiContractOptionsQuery,
  useIiContractPositionOptionsQuery,
  useIiProjectOptionsQuery,
} from "./useInspectionInstructionFacets.js";
import type { IiCascadeOption } from "./types.js";

export interface CascadeValue {
  projectId: string | null;
  contractId: string | null;
  contractPositionId: string | null;
}

export interface ProjectContractPositionCascadeProps {
  value: CascadeValue;
  onChange: (next: CascadeValue) => void;
  /** When true (modal open) the project options load eagerly. */
  enabled: boolean;
  disabled?: boolean;
}

function pickOption(
  options: readonly IiCascadeOption[],
  id: string | null,
): IiCascadeOption | null {
  if (id === null) return null;
  return options.find((o) => o.id === id) ?? null;
}

/** Translate Autocomplete renderInput params into a labelled TextField,
 *  routing the legacy InputProps triplet through `slotProps` so the
 *  raw (optional-prop-bearing) params object is never spread onto
 *  TextField — keeps `exactOptionalPropertyTypes` happy. */
function cascadeInput(
  p: AutocompleteRenderInputParams,
  label: string,
): ReactElement {
  const { InputLabelProps, InputProps, inputProps, ...rest } = p;
  const tfProps = {
    ...rest,
    label,
    required: true,
    slotProps: {
      htmlInput: { ...inputProps, "aria-label": label },
      input: InputProps,
      inputLabel: { ...InputLabelProps, shrink: true },
    },
  } as unknown as React.ComponentProps<typeof TextField>;
  return <TextField {...tfProps} />;
}

export function ProjectContractPositionCascade({
  value,
  onChange,
  enabled,
  disabled = false,
}: ProjectContractPositionCascadeProps): ReactElement {
  const projects = useIiProjectOptionsQuery(enabled);
  const contracts = useIiContractOptionsQuery(value.projectId);
  const positions = useIiContractPositionOptionsQuery(value.contractId);

  const projectOptions = projects.data?.items ?? [];
  const contractOptions = contracts.data?.items ?? [];
  const positionOptions = positions.data?.items ?? [];

  return (
    <Stack spacing={2}>
      <Autocomplete<IiCascadeOption>
        size="small"
        disabled={disabled}
        options={projectOptions}
        value={pickOption(projectOptions, value.projectId)}
        loading={projects.isPending && enabled}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_e, next) => {
          // Changing the Project clears downstream — no lock-in (AC17).
          onChange({
            projectId: next?.id ?? null,
            contractId: null,
            contractPositionId: null,
          });
        }}
        renderInput={(p) => cascadeInput(p, "Projects")}
      />
      <Autocomplete<IiCascadeOption>
        size="small"
        disabled={disabled || value.projectId === null}
        options={contractOptions}
        value={pickOption(contractOptions, value.contractId)}
        loading={contracts.isPending && value.projectId !== null}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_e, next) => {
          // Changing the Contract clears the Position.
          onChange({
            ...value,
            contractId: next?.id ?? null,
            contractPositionId: null,
          });
        }}
        renderInput={(p) => cascadeInput(p, "Contracts")}
      />
      <Autocomplete<IiCascadeOption>
        size="small"
        disabled={disabled || value.contractId === null}
        options={positionOptions}
        value={pickOption(positionOptions, value.contractPositionId)}
        loading={positions.isPending && value.contractId !== null}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_e, next) => {
          onChange({ ...value, contractPositionId: next?.id ?? null });
        }}
        renderInput={(p) => cascadeInput(p, "Contract Position")}
      />
    </Stack>
  );
}
